import { useState, useMemo } from 'react';
import Tooltip from '@/components/Tooltip';
import DeletePopover from '@/components/DeletePopover';
import { renameCandidateGroup, reorderCandidateGroup } from '@/lib/api/communications.api';
import { GROUP_OTHERS } from '../../constants';
import type { Campaign, Influencer } from '../../types';

export function CandidateGroupsFilter({
  campaign, candidateGroups, candidateGroupsLoading, currentGroupName,
  onGroupClick, onCreateGroup, onAddToGroup, onRemoveFromGroup,
  onRemoveGroup, onGroupsCommitted,
  selectedUserIds, influencers, selectedCampaignId, t,
}: {
  campaign: Campaign | null;
  candidateGroups: Record<string, number>;
  candidateGroupsLoading: boolean;
  currentGroupName: string;
  onGroupClick: (name: string) => void;
  onCreateGroup: (userIds: string[], groupName: string) => void;
  onAddToGroup: (userIds: string[], groupName: string) => void;
  onRemoveFromGroup: (userIds: string[]) => void;
  onRemoveGroup: (groupName: string) => void;
  /** Called once after all drafted renames/reorders are committed (see commitDraft) — not
   *  per API call, so the parent only needs to refresh its campaign/group data a single time
   *  instead of once per change (which used to visibly re-fetch/flicker per change). `renames`
   *  lets the parent redirect an active group-name filter that got renamed mid-edit. */
  onGroupsCommitted: (renames: Array<{ from: string; to: string }>) => void;
  selectedUserIds: Set<string>;
  influencers: Influencer[];
  selectedCampaignId: string;
  t: (en: string, ko?: string) => string;
}) {
  const [open, setOpen] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [renameTarget, setRenameTarget] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [showExistingGroupPicker, setShowExistingGroupPicker] = useState(false);
  // Rename/reorder while editing are drafted here (not sent to the API) until "Finish
  // editing" is pressed — null means no pending draft (nothing to save yet).
  const [draftGroups, setDraftGroups] = useState<{ originalName: string; name: string; order: number }[] | null>(null);
  const [saving, setSaving] = useState(false);

  const selectedInfluencers = useMemo(() => {
    return influencers.filter((u) => selectedUserIds.has(u._id));
  }, [influencers, selectedUserIds]);

  const { userIdsToGroup, userIdsToUngroup } = useMemo(() => {
    const toUngroup: string[] = [];
    const toGroup: string[] = [];
    selectedInfluencers.forEach((user) => {
      const uc = user.campaigns?.find((c) => c.campaignId === selectedCampaignId);
      if (uc?.candidateGroupName) {
        toUngroup.push(user._id);
      } else {
        toGroup.push(user._id);
      }
    });
    return { userIdsToGroup: toGroup, userIdsToUngroup: toUngroup };
  }, [selectedInfluencers, selectedCampaignId]);

  const groups = useMemo(() => {
    const defined = (campaign?.candidateGroups ?? [])
      .slice()
      .sort((a, b) => a.order - b.order);
    return [...defined, { name: GROUP_OTHERS, order: Number.MAX_SAFE_INTEGER }];
  }, [campaign]);

  const definedGroups = useMemo(() => groups.filter((g) => g.name !== GROUP_OTHERS), [groups]);

  // What's actually rendered/reordered while editing — the draft if one is in progress,
  // otherwise the live campaign state. Falling back to `groups` (not a fresh draft) when
  // there's nothing pending keeps `displayGroups` a pure derivation of props/state.
  const displayGroups = useMemo(() => {
    const base = draftGroups ?? definedGroups.map((g) => ({ originalName: g.name, name: g.name, order: g.order }));
    return [...base, { originalName: GROUP_OTHERS, name: GROUP_OTHERS, order: Number.MAX_SAFE_INTEGER }];
  }, [draftGroups, definedGroups]);

  const isDirty = draftGroups !== null;

  const ensureDraft = () =>
    draftGroups ?? definedGroups.map((g) => ({ originalName: g.name, name: g.name, order: g.order }));

  const existingGroupNames = useMemo(() => {
    return (campaign?.candidateGroups ?? [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((g) => g.name);
  }, [campaign]);

  if (!campaign) return null;
  if (candidateGroupsLoading) {
    return (
      <div className="filter-section campaign-status-filter">
        <div className="campaign-stats-toggle">
          <i className="fa fa-object-group campaign-stats-toggle-icon" />
          <span>{t('Candidate groups', '후보 그룹')}</span>
        </div>
        <div className="campaign-stats-body text-center py-2 text-xs text-ggc-disabled">
          <i className="fa fa-spinner fa-spin" /> {t('Loading...', '로딩 중...')}
        </div>
      </div>
    );
  }

  const handleNewGroup = () => {
    const name = newGroupName.trim();
    if (!name || name.toUpperCase() === GROUP_OTHERS.toUpperCase()) return;
    onCreateGroup(userIdsToGroup, name);
    setNewGroupName('');
    setShowNewGroupInput(false);
  };

  const handleAddToExisting = (groupName: string) => {
    onAddToGroup(userIdsToGroup, groupName);
    setShowExistingGroupPicker(false);
  };

  const handleRenameConfirm = () => {
    if (!renameTarget || !renameValue.trim()) return;
    const newName = renameValue.trim();
    if (newName.toUpperCase() === GROUP_OTHERS.toUpperCase()) return;
    const target = renameTarget;
    setDraftGroups(ensureDraft().map((g) => (g.name === target ? { ...g, name: newName } : g)));
    setRenameTarget(null);
    setRenameValue('');
  };

  const handleReorderDraft = (name: string, direction: 'up' | 'down') => {
    const base = ensureDraft();
    const idx = base.findIndex((g) => g.name === name);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= base.length) return;
    const next = base.slice();
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setDraftGroups(next.map((g, i) => ({ ...g, order: i })));
  };

  /**
   * Renames go first (identified by their original name), then reorders — computed as the
   * minimal swap sequence from the pre-edit order (with renames substituted in, since the DB
   * now reflects them) to the drafted order. Each swap is exactly two named groups (see
   * reorderCandidateGroup.action.ts), so any permutation is reachable this way. Calls the API
   * functions directly (not the parent's onRenameGroup/onReorderGroup-style handlers) so no
   * refresh happens per call — the parent refreshes once via onGroupsCommitted when this whole
   * batch is done, instead of once per change (which used to re-fetch/flicker per change).
   */
  const commitDraft = async () => {
    if (!draftGroups) return;
    for (const g of draftGroups) {
      if (g.name !== g.originalName) {
        await renameCandidateGroup({ campaignId: selectedCampaignId, groupName: g.originalName, newGroupName: g.name });
      }
    }
    const renameMap = new Map(draftGroups.map((g) => [g.originalName, g.name]));
    const working = definedGroups.map((g) => renameMap.get(g.name) ?? g.name);
    const target = draftGroups.map((g) => g.name);
    for (let i = 0; i < target.length; i++) {
      if (working[i] === target[i]) continue;
      const j = working.indexOf(target[i], i);
      if (j === -1) continue;
      await reorderCandidateGroup({ campaignId: selectedCampaignId, groupName: working[i], replacedGroupName: working[j] });
      [working[i], working[j]] = [working[j], working[i]];
    }
  };

  const handleToggleEditMode = async () => {
    if (editMode && isDirty) {
      setSaving(true);
      const renames = draftGroups
        .filter((g) => g.name !== g.originalName)
        .map((g) => ({ from: g.originalName, to: g.name }));
      try {
        await commitDraft();
        setDraftGroups(null);
        setEditMode(false);
        onGroupsCommitted(renames);
      } catch {
        // Keep the draft + edit mode open so the user can retry instead of losing their edits.
      } finally {
        setSaving(false);
      }
      return;
    }
    setDraftGroups(null);
    setEditMode((v) => !v);
  };

  return (
    <div className="filter-section campaign-status-filter">
      {/* 헤더: 캐럿 버튼 클릭으로 접기/펼치기, 편집 버튼 별도 */}
      <div className="candidate-groups-header">
        <div className="candidate-groups-header-main">
          <i className="fa fa-object-group campaign-stats-toggle-icon" />
          <span>{t('Candidate groups', '후보 그룹')}</span>
        </div>
        <Tooltip content={t('Click to expand or collapse', '클릭하면 펼치기/접기가 가능합니다')}>
          <button
            type="button"
            className="candidate-groups-caret-btn"
            onClick={() => setOpen((v) => !v)}
          >
            <i className={`fa fa-caret-${open ? 'up' : 'down'}`} />
          </button>
        </Tooltip>
        <Tooltip content={
          editMode
            ? (isDirty ? t('You have unsaved changes — click to save', '저장하지 않은 변경사항이 있습니다 — 버튼을 눌러서 저장하세요') : t('Finish editing', '편집 완료'))
            : t('Edit candidate groups', '후보 그룹 편집')
        }>
          <button
            type="button"
            className={`candidate-groups-edit-btn${isDirty ? ' candidate-groups-edit-btn--dirty' : ''}`}
            onClick={() => void handleToggleEditMode()}
            disabled={saving}
          >
            <i className={`fa fa-${saving ? 'spinner fa-spin' : editMode ? 'check' : 'pencil'}`} />
          </button>
        </Tooltip>
      </div>

      {open && (
        <div className="campaign-stats-body">
          <div className={`candidate-groups-list${editMode ? ' candidate-groups-list--edit' : ''}`}>
            {displayGroups.map((group, index) => {
              const isSelected = currentGroupName === group.name;
              const count = candidateGroups[group.name] ?? 0;
              const isOthers = group.name === GROUP_OTHERS;
              const prevGroup = !isOthers && index > 0 ? displayGroups[index - 1] : null;
              const nextGroup = !isOthers && index < displayGroups.length - 1 && displayGroups[index + 1].name !== GROUP_OTHERS
                ? displayGroups[index + 1] : null;

              if (renameTarget === group.name) {
                return (
                  <div key={group.name} className="candidate-group-rename">
                    <input
                      className="candidate-group-rename-input"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRenameConfirm(); if (e.key === 'Escape') setRenameTarget(null); }}
                      autoFocus
                    />
                    <button type="button" className="candidate-group-rename-ok" onClick={handleRenameConfirm}><i className="fa fa-check" /></button>
                    <button type="button" className="candidate-group-rename-cancel" onClick={() => setRenameTarget(null)}><i className="fa fa-times" /></button>
                  </div>
                );
              }

              return (
                <Tooltip key={group.name} content={`${group.name.toUpperCase()} (${count} ${t('influencers', '인플루언서')})`} placement="right">
                  <div
                    className={`candidate-group-item${isSelected ? ' candidate-group-item--selected' : ''}${!editMode ? ' cursor-pointer' : ''}`}
                    onClick={() => { if (!editMode) onGroupClick(group.name); }}
                  >
                    <span className="candidate-group-name">{group.name.toUpperCase()}</span>
                    {!editMode && (
                      <span className={`candidate-group-count${isSelected ? ' candidate-group-count--selected' : ''}`}>{count}</span>
                    )}
                    {editMode && !isOthers && (
                      <span className="candidate-group-actions">
                        <Tooltip content={t('Rename', '이름 변경')}>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setRenameTarget(group.name); setRenameValue(group.name); }}><i className="fa fa-pencil" /></button>
                        </Tooltip>
                        <Tooltip content={t('Move up', '위로')}>
                          <button type="button" disabled={!prevGroup} onClick={(e) => { e.stopPropagation(); if (prevGroup) handleReorderDraft(group.name, 'up'); }}><i className="fa fa-caret-up" /></button>
                        </Tooltip>
                        <Tooltip content={t('Move down', '아래로')}>
                          <button type="button" disabled={!nextGroup} onClick={(e) => { e.stopPropagation(); if (nextGroup) handleReorderDraft(group.name, 'down'); }}><i className="fa fa-caret-down" /></button>
                        </Tooltip>
                        <DeletePopover
                          description={t(`Remove group "${group.name}"?`, `"${group.name}" 그룹을 삭제하시겠습니까?`)}
                          onConfirm={async () => { setDraftGroups(null); onRemoveGroup(group.name); }}
                        >
                          <Tooltip essential content={t('Remove', '제거')}>
                            <button type="button" className="candidate-group-remove-btn">
                              <i className="fa fa-times" />
                            </button>
                          </Tooltip>
                        </DeletePopover>
                      </span>
                    )}
                  </div>
                </Tooltip>
              );
            })}
          </div>

          {/* Group 버튼 */}
          {userIdsToGroup.length > 0 && (
            <div className="candidate-group-btn-area">
              <button
                type="button"
                className="candidate-group-action-btn"
                onClick={() => {
                  if (existingGroupNames.length > 0) {
                    setShowExistingGroupPicker(true);
                  } else {
                    setShowNewGroupInput(true);
                  }
                }}
              >
                <i className="fa fa-object-group" /> {t(`Group ${userIdsToGroup.length} influencers`, `${userIdsToGroup.length}명 그룹 지정`)}
              </button>

              {showExistingGroupPicker && (
                <div className="candidate-group-picker">
                  <div className="candidate-group-picker-title">{t('Select group', '그룹 선택')}</div>
                  {existingGroupNames.map((name) => (
                    <button key={name} type="button" className="candidate-group-picker-item" onClick={() => handleAddToExisting(name)}>
                      {name}
                    </button>
                  ))}
                  <div className="candidate-group-picker-divider" />
                  <button type="button" className="candidate-group-picker-item candidate-group-picker-new" onClick={() => { setShowExistingGroupPicker(false); setShowNewGroupInput(true); }}>
                    <i className="fa fa-plus" /> {t('New group', '새 그룹')}
                  </button>
                  <button type="button" className="candidate-group-picker-cancel" onClick={() => setShowExistingGroupPicker(false)}>
                    {t('Cancel', '취소')}
                  </button>
                </div>
              )}

              {showNewGroupInput && (
                <div className="candidate-group-new-input">
                  <input
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleNewGroup(); if (e.key === 'Escape') setShowNewGroupInput(false); }}
                    placeholder={t('New group name...', '새 그룹 이름...')}
                    autoFocus
                  />
                  <button type="button" onClick={handleNewGroup}><i className="fa fa-check" /></button>
                  <button type="button" onClick={() => setShowNewGroupInput(false)}><i className="fa fa-times" /></button>
                </div>
              )}
            </div>
          )}

          {/* Ungroup 버튼 */}
          {userIdsToUngroup.length > 0 && (
            <DeletePopover
              description={t(`Ungroup ${userIdsToUngroup.length} influencers?`, `${userIdsToUngroup.length}명 그룹 해제하시겠습니까?`)}
              onConfirm={async () => { onRemoveFromGroup(userIdsToUngroup); }}
            >
              <button
                type="button"
                className="candidate-group-action-btn candidate-group-ungroup-btn"
              >
                <i className="fa fa-object-ungroup" /> {t(`Ungroup ${userIdsToUngroup.length} influencers`, `${userIdsToUngroup.length}명 그룹 해제`)}
              </button>
            </DeletePopover>
          )}
        </div>
      )}
    </div>
  );
}
